import {
  Component,
  effect,
  forwardRef,
  input,
  model,
  OnDestroy,
  OnInit,
  output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { UmssInputComponent } from '../input/input.component';

import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'umss-search-bar',
  imports: [UmssInputComponent, ReactiveFormsModule],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssSearchBarComponent),
      multi: true
    }
  ]
})
export class UmssSearchBarComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  defaultValue = model<string | null>(null);
  placeholder = input<string | null>(null);

  searchFormControl: FormControl<string | null> = new FormControl<
    string | null
  >(null);
  readonly search = output<string | null>();
  private onDestroy = new Subject<void>();

  private onChange: (value: string | null) => void = () => {
    return;
  };
  private onTouched: () => void = () => {
    return;
  };

  constructor() {
    effect(() => {
      const val = this.defaultValue();
      if (this.searchFormControl.value !== val) {
        this.searchFormControl.setValue(val, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.listenValueChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  onClear(): void {
    this.searchFormControl.setValue(null);
  }

  writeValue(value: string | null): void {
    this.searchFormControl.setValue(value, { emitEvent: false });
    if (this.defaultValue() !== value) {
      this.defaultValue.set(value);
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchFormControl.disable({ emitEvent: false });
    } else {
      this.searchFormControl.enable({ emitEvent: false });
    }
  }

  private listenValueChanges(): void {
    this.searchFormControl.valueChanges
      .pipe(
        takeUntil(this.onDestroy),
        debounceTime(250),
        distinctUntilChanged()
      )
      .subscribe({
        next: (value: string | null) => {
          this.onChange(value);
          this.defaultValue.set(value);

          if (value === null || value.trim() === '') {
            this.search.emit(null);
            return;
          }
          this.search.emit(value);
        }
      });
  }
}
